/**
 * Pure validation functions extracted from server actions.
 * These functions contain no side effects and can be easily unit tested.
 */

/**
 * Latest instant picks can lock: when the award show starts.
 */
export function getLatestVotingDeadline(
  showStartTime: Date | string | null,
): Date | null {
  if (!showStartTime) {
    return null;
  }

  const start = new Date(showStartTime);
  return Number.isNaN(start.getTime()) ? null : start;
}

/**
 * Checks whether voting is already closed for a show before party creation.
 */
export function isVotingClosedForShow(
  showStartTime: Date | string | null,
  currentTime: Date = new Date(),
): boolean {
  const latestVotingDeadline = getLatestVotingDeadline(showStartTime);
  return latestVotingDeadline ? currentTime >= latestVotingDeadline : false;
}

/**
 * Validates that voting closes by the time the award show starts.
 */
export function validateVotingDeadline(
  votingClosesAt: Date | string,
  showStartTime: Date | string | null
): { isValid: boolean; error?: string } {
  const latestAllowedTime = getLatestVotingDeadline(showStartTime);
  const closesAt = new Date(votingClosesAt);

  if (!latestAllowedTime) {
    return { isValid: true };
  }

  if (closesAt > latestAllowedTime) {
    return {
      isValid: false,
      error: "Voting must close by the time the award show starts",
    };
  }

  return { isValid: true };
}

export type VotingDeadlineInstant = Date | string | null | undefined;

export interface VotingDeadlineSource {
  /** Stored override. Null means the party locks when the show starts. */
  votingDeadlineOverride: VotingDeadlineInstant;
  showStartTime: VotingDeadlineInstant;
}

function parseVotingInstant(value: VotingDeadlineInstant): Date | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Effective voting deadline.
 *
 * A null override follows the show start. A custom override is kept only
 * while it is still at or before the show start. If the show moves earlier,
 * the effective instant clamps to the new start without rewriting the
 * stored override, so a later show-start change can restore it.
 */
export function resolveEffectiveVotingDeadline(
  source: VotingDeadlineSource,
): Date | null {
  const showStart = parseVotingInstant(source.showStartTime);
  const override = parseVotingInstant(source.votingDeadlineOverride);

  if (!override) {
    return showStart;
  }
  if (!showStart || override.getTime() <= showStart.getTime()) {
    return override;
  }
  return showStart;
}

export type VotingDeadlineActor = "host" | "admin";

export type VotingDeadlineUpdate =
  | { mode: "follow-show" }
  | { mode: "custom"; closesAt: Date | string };

export type VotingDeadlineUpdateError =
  | "customDeadlineRequiresPartyPass"
  | "deadlineAfterShowStart"
  | "invalidDeadline";

export type VotingDeadlineChangeResult =
  | { ok: true; override: Date | null }
  | { ok: false; error: VotingDeadlineUpdateError };

/**
 * Server-side rule for changing a party's stored deadline override.
 * Free hosts cannot set a custom time. Party Pass hosts and admins can,
 * but never after the show starts. Follow-the-show clears the override.
 */
export function validateVotingDeadlineChange(input: {
  plan: string | null | undefined;
  actor: VotingDeadlineActor;
  showStartTime: VotingDeadlineInstant;
  update: VotingDeadlineUpdate;
}): VotingDeadlineChangeResult {
  if (input.update.mode === "follow-show") {
    return { ok: true, override: null };
  }

  const closesAt = parseVotingInstant(input.update.closesAt);
  if (!closesAt) {
    return { ok: false, error: "invalidDeadline" };
  }

  const isPass = input.plan === "party_pass";
  if (input.actor === "host" && !isPass) {
    return { ok: false, error: "customDeadlineRequiresPartyPass" };
  }

  const showStart = parseVotingInstant(input.showStartTime);
  if (showStart && closesAt.getTime() > showStart.getTime()) {
    return { ok: false, error: "deadlineAfterShowStart" };
  }

  return { ok: true, override: closesAt };
}

/**
 * Checks if a party is locked based on manual lock or an already resolved
 * deadline. Pass the result of resolveEffectiveVotingDeadline, not the raw
 * override column.
 */
export function isPartyLocked(
  isManuallyLocked: boolean,
  effectiveVotingDeadline: VotingDeadlineInstant,
  currentTime: Date = new Date(),
): boolean {
  if (isManuallyLocked) {
    return true;
  }
  const deadline = parseVotingInstant(effectiveVotingDeadline);
  return deadline ? currentTime >= deadline : false;
}

/**
 * Lock check for a party row. This is the only lock path that reads an
 * override together with the show start.
 */
export function isPartyVotingLocked(
  input: VotingDeadlineSource & {
    isManuallyLocked: boolean;
    currentTime?: Date;
  },
): boolean {
  return isPartyLocked(
    input.isManuallyLocked,
    resolveEffectiveVotingDeadline(input),
    input.currentTime ?? new Date(),
  );
}

/**
 * Checks if a user is a placeholder (invited but not yet signed up).
 */
export function isPlaceholderUser(clerkId: string): boolean {
  return clerkId.startsWith("pending:");
}

/**
 * A pending email invite is only outstanding if that address has not already
 * joined as a real (non-placeholder) party member.
 */
export function isOutstandingPartyInvitation(
  invitation: { status: string; email: string; userId?: string | null },
  participants: Array<{
    userId?: string;
    user: { email: string; clerkId: string };
  }>,
): boolean {
  if (invitation.status !== "PENDING") {
    return false;
  }

  const invitationEmail = normalizeEmail(invitation.email);

  return !participants.some((participant) => {
    if (isPlaceholderUser(participant.user.clerkId)) {
      return false;
    }

    if (
      invitation.userId &&
      participant.userId &&
      invitation.userId === participant.userId
    ) {
      return true;
    }

    return (
      invitationEmail.length > 0 &&
      normalizeEmail(participant.user.email) === invitationEmail
    );
  });
}

/**
 * Checks if a Clerk invitation ID represents a real Clerk invitation.
 */
export function isRealClerkInvitation(clerkInvitationId: string): boolean {
  return (
    !clerkInvitationId.startsWith("existing:") &&
    !clerkInvitationId.startsWith("local:")
  );
}

/**
 * Validates party creation form data.
 */
export function validatePartyCreation(
  name: string | null,
  awardShowId: string | null,
  votingClosesAt: Date | null
): { isValid: boolean; error?: string } {
  if (!name || name.trim() === "") {
    return { isValid: false, error: "Party name is required" };
  }

  if (!awardShowId || awardShowId.trim() === "") {
    return { isValid: false, error: "Award show is required" };
  }

  if (!votingClosesAt) {
    return { isValid: false, error: "Voting deadline is required" };
  }

  if (isNaN(votingClosesAt.getTime())) {
    return { isValid: false, error: "Invalid voting deadline date" };
  }

  return { isValid: true };
}

/**
 * Validates award show creation form data.
 */
export function validateAwardShowCreation(
  name: string | null,
  year: number | null
): { isValid: boolean; error?: string } {
  if (!name || name.trim() === "") {
    return { isValid: false, error: "Name is required" };
  }

  if (year === null || isNaN(year)) {
    return { isValid: false, error: "Year is required" };
  }

  if (year < 1900 || year > 2100) {
    return { isValid: false, error: "Year must be between 1900 and 2100" };
  }

  return { isValid: true };
}

/**
 * Validates category creation form data.
 */
export function validateCategoryCreation(
  awardShowId: string | null,
  name: string | null
): { isValid: boolean; error?: string } {
  if (!awardShowId || awardShowId.trim() === "") {
    return { isValid: false, error: "Award show is required" };
  }

  if (!name || name.trim() === "") {
    return { isValid: false, error: "Category name is required" };
  }

  return { isValid: true };
}

/**
 * Validates nominee creation form data.
 */
export function validateNomineeCreation(
  categoryId: string | null,
  name: string | null
): { isValid: boolean; error?: string } {
  if (!categoryId || categoryId.trim() === "") {
    return { isValid: false, error: "Category is required" };
  }

  if (!name || name.trim() === "") {
    return { isValid: false, error: "Nominee name is required" };
  }

  return { isValid: true };
}

/**
 * Validates email format.
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates URL format.
 */
export function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Normalizes an email address (lowercase, trimmed).
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}
